import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, ExternalLink, MessagesSquare, Pencil, FileText, Plus, FileStack, Lock, Search, X as XIcon } from 'lucide-react';
import { DateRangePicker } from 'rsuite';
import HiloVersionesModal from './HiloVersionesModal';
import KanbanLegend from './KanbanLegend';
import { Tooltip } from './ui';
import api, { tiposServicioAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { generatePdfCotizacion } from '../utils/pdfGenerator';
import { totalConRentabilidad, rowTotalConVariaciones, aplicarVariaciones } from '../utils/helpers';
import { useModalKeyboard } from '../hooks/useModalKeyboard';

// Estados finales: aprobada y rechazada no se pueden cambiar mas (salvo superadmin)
const ESTADOS_FINALES = ['aprobada', 'rechazada'];

const columnas = [
    { id: 'borrador', label: 'Borrador', color: '#9ca3af' },
    { id: 'enviada', label: 'Enviada', color: '#60a5fa' },
    { id: 'aprobada', label: 'Aprobada', color: '#34d399' },
    { id: 'rechazada', label: 'Rechazada', color: '#f87171' }
];

const MisCotizaciones = ({ configuracion }) => {
    const navigate = useNavigate();
    const { user, tienePermiso } = useAuth();
    const verDetalles = tienePermiso('ver_detalles_costos');
    const verPdf = tienePermiso('ver_pdf');
    const priceClass = verDetalles ? '' : 'price-hidden';
    const blurPrice = (value, moneda) => verDetalles ? formatCurrency(value, moneda) : '•••••';
    const [cotizaciones, setCotizaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [modalCotizacion, setModalCotizacion] = useState(null);
    const [loadingModal, setLoadingModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [hiloModal, setHiloModal] = useState(null);
    // Filtro temporal del kanban:
    //   '30d' | 'mes' | 'todo' | 'personalizado' (usa rangoPersonalizado)
    const [filtroVista, setFiltroVista] = useState('30d');
    const [rangoPersonalizado, setRangoPersonalizado] = useState(null); // [Date, Date] | null
    const [busqueda, setBusqueda] = useState('');

    // Alertas
    const [alertasConfig, setAlertasConfig] = useState([]);
    const [tiposActividad, setTiposActividad] = useState([]);
    const [actividadModal, setActividadModal] = useState(null);
    const [actividadForm, setActividadForm] = useState({
        tipo_actividad_id: null,
        descripcion: '',
        estado_alerta: 'comunicacion_reciente'
    });
    const [savingActividad, setSavingActividad] = useState(false);
    const [historial, setHistorial] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            const [cotRes, configRes, tiposRes] = await Promise.all([
                api.cotizaciones.getMyCotizaciones(),
                api.alertas.getConfig(),
                api.alertas.getTiposActividad()
            ]);
            setCotizaciones(cotRes.data || []);
            setAlertasConfig(configRes.data || []);
            setTiposActividad((tiposRes.data || []).filter(t => t.activo));
        } catch (err) {
            console.error('Error al cargar cotizaciones:', err);
            toast.error('Error al cargar mis cotizaciones');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, moneda = 'PEN') => {
        const symbol = moneda === 'USD' ? '$ ' : 'S/ ';
        return symbol + parseFloat(amount || 0).toLocaleString('es-PE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getAlertaColor = (clave) => {
        const config = alertasConfig.find(a => a.clave === clave);
        return config?.color || '#9ca3af';
    };

    const getAlertaNombre = (clave) => {
        const config = alertasConfig.find(a => a.clave === clave);
        return config?.nombre || clave;
    };

    // Filtro unico + busqueda. Cada opcion de filtroVista es mutuamente exclusiva.
    // parseFechaCot: normaliza cot.fecha — puede venir "YYYY-MM-DD" o ISO completo
    // ("YYYY-MM-DDTHH:MM:SS.sssZ"). Tomamos solo la parte YYYY-MM-DD y forzamos
    // hora 00:00 LOCAL para comparaciones consistentes con hace30/inicioMes.
    const parseFechaCot = (raw) => {
        if (!raw) return null;
        const ymd = String(raw).split('T')[0];
        const d = new Date(ymd + 'T00:00:00');
        return isNaN(d.getTime()) ? null : d;
    };

    const cotizacionesFiltradas = (() => {
        const q = busqueda.trim().toLowerCase();
        const ahora = new Date();
        const hace30 = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() - 30);
        const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        return cotizaciones.filter(cot => {
            const f = parseFechaCot(cot.fecha);
            // Sin fecha valida no puede pasar filtros temporales (excepto "todo").
            if (filtroVista === '30d') {
                if (!f || f < hace30) return false;
            } else if (filtroVista === 'mes') {
                if (!f || f < inicioMes) return false;
            } else if (filtroVista === 'personalizado' && rangoPersonalizado) {
                const [desde, hasta] = rangoPersonalizado;
                if (!f) return false;
                if (desde && f < new Date(desde.getFullYear(), desde.getMonth(), desde.getDate())) return false;
                if (hasta && f > new Date(hasta.getFullYear(), hasta.getMonth(), hasta.getDate(), 23, 59, 59)) return false;
            }
            // filtroVista === 'todo' → sin filtro adicional
            // Busqueda adicional (aplica sobre lo ya filtrado).
            if (q) {
                const hay = [cot.numero, cot.cliente_nombre, cot.cliente_empresa]
                    .filter(Boolean)
                    .some(x => String(x).toLowerCase().includes(q));
                if (!hay) return false;
            }
            return true;
        });
    })();

    const getCotizacionesByEstado = (estado) => {
        return cotizacionesFiltradas.filter(cot => cot.estado === estado);
    };

    // Bypass: permiso explicito (configurable por rol) o superadmin de seguridad.
    const puedeDesbloquear = tienePermiso('desbloquear_estado') || user?.rol === 'superadmin';
    const esEstadoFinal = (estado) => ESTADOS_FINALES.includes(estado) && !puedeDesbloquear;

    const handleDragStart = (e, cotId) => {
        if (isUpdating) { e.preventDefault(); return; }
        // Defensa adicional: si la card es final y el usuario no es superadmin,
        // no permitir iniciar drag (el atributo draggable=false ya lo bloquea
        // pero esto cubre casos de browsers permisivos).
        const cot = cotizaciones.find(c => c.id === cotId);
        if (cot && esEstadoFinal(cot.estado)) { e.preventDefault(); return; }
        setDraggingId(cotId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', cotId.toString());
    };

    const handleDragEnd = () => {
        setDraggingId(null);
        setDragOverColumn(null);
    };

    const handleDragOver = (e, columnaId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverColumn !== columnaId) setDragOverColumn(columnaId);
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOverColumn(null);
    };

    const handleDrop = async (e, nuevoEstado) => {
        e.preventDefault();
        const cotId = parseInt(e.dataTransfer.getData('text/plain'));
        setDraggingId(null);
        setDragOverColumn(null);
        if (isUpdating || !cotId) return;
        const cotizacion = cotizaciones.find(c => c.id === cotId);
        if (!cotizacion || cotizacion.estado === nuevoEstado) return;
        const estadoAnterior = cotizacion.estado;
        setIsUpdating(true);
        setCotizaciones(prev => prev.map(c => c.id === cotId ? { ...c, estado: nuevoEstado } : c));
        try {
            await api.cotizaciones.updateEstado(cotId, nuevoEstado);
            toast.success(`Cotizacion movida a "${columnas.find(c => c.id === nuevoEstado)?.label}"`);
        } catch (err) {
            toast.error('Error al actualizar estado');
            setCotizaciones(prev => prev.map(c => c.id === cotId ? { ...c, estado: estadoAnterior } : c));
        } finally {
            setIsUpdating(false);
        }
    };

    const handleVerCotizacion = async (cotId) => {
        setLoadingModal(true);
        try {
            const response = await api.cotizaciones.getById(cotId);
            setModalCotizacion(response.data);
        } catch (err) {
            toast.error('Error al cargar detalles');
        } finally {
            setLoadingModal(false);
        }
    };

    const closeModal = () => setModalCotizacion(null);

    const handleGenerarPdf = async (cotId, preview = false) => {
        try {
            const response = await api.cotizaciones.getById(cotId);
            // Cotizacion con archivo adjunto: usar ese archivo, no el PDF del sistema.
            if (response.data?.archivo_path) {
                if (preview) {
                    await api.cotizaciones.verArchivo(cotId);
                } else {
                    await api.cotizaciones.descargarArchivo(cotId);
                }
                return;
            }
            const { servicios: serviciosData, extras, ...cotizacionBase } = response.data;
            const tiposRes = await tiposServicioAPI.getAll();

            const igvRate = parseFloat(configuracion?.igv_rate) || 0.18;
            const rentabilidad = parseFloat(configuracion?.rentabilidad) || 0.68;

            const servicios = (serviciosData || []).map(s => {
                const asignaciones = (s.asignaciones || []).map(a => {
                    const horas = parseFloat(a.horas) || 0;
                    const costoHora = parseFloat(a.costo_hora) || 0;
                    const horasAdicionales = horas * 0.12;
                    return { ...a, horas_adicionales: horasAdicionales, subtotal: (horas + horasAdicionales) * costoHora };
                });
                const costoServicio = asignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
                // Preservar s.total del backend (que ya viene con override + variaciones
                // aplicados). Solo re-calculamos si el backend no lo trajo (defensa).
                const total = parseFloat(s.total) || totalConRentabilidad(costoServicio, rentabilidad);
                return { ...s, asignaciones, costo: costoServicio, total };
            });

            const subtotalServicios = servicios.reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
            const subtotalExtras = (extras || []).reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
            const subtotal = subtotalServicios;
            const aplicaIgv = cotizacionBase.incluye_igv !== false && cotizacionBase.incluye_igv !== 0;
            const igv = aplicaIgv ? subtotal * igvRate : 0;
            const total = subtotal + igv + subtotalExtras;

            await generatePdfCotizacion({
                cotizacion: { ...cotizacionBase, subtotal, subtotalExtras, igv, total },
                servicios,
                extras,
                configuracion: configuracion || {},
                tiposServicio: tiposRes.data || [],
                preview
            });
        } catch (err) {
            toast.error('Error al generar PDF: ' + err.message);
        }
    };

    // ============ ACTIVIDADES ============
    const abrirActividadModal = async (cot) => {
        setActividadModal(cot);
        setActividadForm({ tipo_actividad_id: null, descripcion: '', estado_alerta: 'comunicacion_reciente' });
        setLoadingHistorial(true);
        try {
            const res = await api.alertas.getActividades(cot.id);
            setHistorial(res.data || []);
        } catch (err) {
            setHistorial([]);
        } finally {
            setLoadingHistorial(false);
        }
    };

    const cerrarActividadModal = () => {
        setActividadModal(null);
        setHistorial([]);
    };

    // a11y: Escape cierra modales + bloquea scroll del body
    useModalKeyboard({ open: !!actividadModal, onClose: cerrarActividadModal });
    useModalKeyboard({ open: !!modalCotizacion, onClose: closeModal });

    const guardarActividad = async () => {
        if (!actividadForm.tipo_actividad_id) {
            toast.error('Selecciona un tipo de actividad');
            return;
        }
        setSavingActividad(true);
        try {
            const res = await api.alertas.registrarActividad(actividadModal.id, actividadForm);
            setHistorial(prev => [res.data, ...prev]);
            setCotizaciones(prev => prev.map(c =>
                c.id === actividadModal.id
                    ? { ...c, ultima_alerta_estado: actividadForm.estado_alerta, ultima_alerta_fecha: new Date().toISOString(), ultima_actividad_tipo: res.data.tipo_nombre, total_actividades: (c.total_actividades || 0) + 1 }
                    : c
            ));
            setActividadForm({ tipo_actividad_id: null, descripcion: '', estado_alerta: 'comunicacion_reciente' });
            toast.success('Actividad registrada');
        } catch (err) {
            toast.error('Error al registrar actividad');
        } finally {
            setSavingActividad(false);
        }
    };

    if (loading) {
        return <div className="loading">Cargando mis cotizaciones...</div>;
    }

    return (
        <div className="kanban-container">
            <div className="kanban-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div>
                        <h2 style={{ margin: 0 }}>Mis Cotizaciones</h2>
                        <span style={{ color: 'var(--muted)', fontSize: '14px' }}>{user?.nombre}</span>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {tienePermiso('gestion_cotizaciones') && (
                        <Tooltip content="Crear una nueva cotización">
                            <button
                                className="btn neon"
                                onClick={() => navigate('/cotizaciones/nueva')}
                            >
                                <Plus size={16} style={{ marginRight: '6px' }} />
                                Nueva Cotización
                            </button>
                        </Tooltip>
                    )}
                    <span style={{ color: 'var(--muted)', fontSize: '14px' }}>
                        {cotizacionesFiltradas.length} de {cotizaciones.length}
                    </span>
                </div>
            </div>

            {/* Barra de filtros + búsqueda del kanban */}
            <div className="kanban-filters">
                <div className="kanban-filters-chips" role="group" aria-label="Filtro de vista">
                    <button
                        type="button"
                        className={`kanban-chip ${filtroVista === '30d' ? 'is-active' : ''}`}
                        onClick={() => setFiltroVista('30d')}
                        title="Cotizaciones de los últimos 30 días"
                    >
                        Últimos 30 días
                    </button>
                    <button
                        type="button"
                        className={`kanban-chip ${filtroVista === 'mes' ? 'is-active' : ''}`}
                        onClick={() => setFiltroVista('mes')}
                        title="Todas las cotizaciones del mes calendario actual"
                    >
                        Este mes
                    </button>
                    <button
                        type="button"
                        className={`kanban-chip ${filtroVista === 'todo' ? 'is-active' : ''}`}
                        onClick={() => setFiltroVista('todo')}
                        title="Todas las cotizaciones sin filtro"
                    >
                        Todo
                    </button>
                    <button
                        type="button"
                        className={`kanban-chip ${filtroVista === 'personalizado' ? 'is-active' : ''}`}
                        onClick={() => setFiltroVista('personalizado')}
                        title="Elige un rango de fechas específico"
                    >
                        Personalizado
                    </button>
                    {filtroVista === 'personalizado' && (
                        <DateRangePicker
                            value={rangoPersonalizado}
                            onChange={setRangoPersonalizado}
                            format="dd/MM/yyyy"
                            character=" – "
                            placeholder="Seleccionar rango"
                            showOneCalendar
                            placement="bottomStart"
                            size="sm"
                            cleanable
                            ranges={[]}
                        />
                    )}
                </div>
                <div className="kanban-search">
                    <Search size={14} className="kanban-search-icon" aria-hidden="true" />
                    <input
                        type="text"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="Buscar por número, cliente o empresa…"
                        className="kanban-search-input"
                    />
                    {busqueda && (
                        <button
                            type="button"
                            onClick={() => setBusqueda('')}
                            className="kanban-search-clear"
                            aria-label="Limpiar búsqueda"
                        >
                            <XIcon size={13} />
                        </button>
                    )}
                </div>
            </div>

            <div className="kanban-board">
                {columnas.map((columna) => {
                    const items = getCotizacionesByEstado(columna.id);
                    const isOver = dragOverColumn === columna.id;
                    return (
                        <div
                            key={columna.id}
                            className={`kanban-column ${isOver ? 'drag-over' : ''}`}
                            onDragOver={(e) => handleDragOver(e, columna.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, columna.id)}
                        >
                            <div className="kanban-column-header" style={{ '--column-color': columna.color }}>
                                <span className="kanban-column-title">{columna.label}</span>
                                <span className="kanban-column-count">{items.length}</span>
                            </div>
                            <div className="kanban-column-body">
                                {items.length === 0 ? (
                                    <div className="kanban-empty">Sin cotizaciones</div>
                                ) : (
                                    items.map((cot) => {
                                        const final = esEstadoFinal(cot.estado);
                                        return (
                                        <Tooltip key={cot.id} content={final ? 'Bloqueado — no se puede mover' : null}>
                                        <div
                                            className={`kanban-card ${draggingId === cot.id ? 'dragging' : ''} ${isUpdating ? 'updating' : ''} ${final ? 'is-final' : ''}`}
                                            draggable={!isUpdating && !final}
                                            onDragStart={(e) => handleDragStart(e, cot.id)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <div className="kanban-card-header">
                                                <div className="kanban-card-numero-wrap">
                                                    {cot.prefijo_color && (
                                                        <span
                                                            className="prefijo-dot"
                                                            style={{ backgroundColor: cot.prefijo_color }}
                                                            title={cot.prefijo_nombre || cot.prefijo_str}
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                    <span className="kanban-card-numero">{cot.numero}</span>
                                                    {final && (
                                                        <Lock size={14} className="kanban-card-lock" aria-hidden="true" />
                                                    )}
                                                    {cot.versiones_count > 1 && (
                                                        <Tooltip content={`Ver ${cot.versiones_count} versiones para esta cotización`}>
                                                            <button
                                                                type="button"
                                                                className="versiones-badge"
                                                                onClick={(e) => { e.stopPropagation(); setHiloModal(cot.id); }}
                                                                aria-label={`Ver hilo de versiones (${cot.versiones_count})`}
                                                            >
                                                                <FileStack size={11} aria-hidden="true" />
                                                                {cot.versiones_count}
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                    {cot.cliente_empresa && (
                                                        <span className="kanban-card-empresa-badge">
                                                            {cot.cliente_empresa.toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="kanban-card-fecha">
                                                    {new Date(cot.fecha || cot.created_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </span>
                                            </div>
                                            <div className="kanban-card-cliente">
                                                {cot.cliente_nombre}
                                            </div>
                                            {(() => {
                                                // Total combinado = proyecto (cot.total ya excluye opcionales)
                                                // + total opcionales con su propio IGV. Solo mostramos desglose
                                                // si hay opcionales.
                                                const subtotalOpc = parseFloat(cot.subtotal_opcionales) || 0;
                                                const aplicaIgv = cot.incluye_igv !== false && cot.incluye_igv !== 0;
                                                const igvRate = parseFloat(configuracion?.igv_rate) || 0.18;
                                                const totalOpc = subtotalOpc + (aplicaIgv ? subtotalOpc * igvRate : 0);
                                                const totalProy = parseFloat(cot.total) || 0;
                                                const totalCombinado = totalProy + totalOpc;
                                                const hayOpc = subtotalOpc > 0;
                                                return (
                                                    <div className={`kanban-card-total ${priceClass}`}>
                                                        {blurPrice(totalCombinado, cot.moneda)}
                                                        {hayOpc && (
                                                            <div className="kanban-card-desglose">
                                                                <span>Proyecto: <strong>{blurPrice(totalProy, cot.moneda)}</strong></span>
                                                                <span>Opcionales: <strong>{blurPrice(totalOpc, cot.moneda)}</strong></span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {/* Indicador de alerta - solo para "enviada" */}
                                            {(cot.ultima_alerta_estado || cot.total_actividades > 0) && (
                                                <div className="kanban-card-separator" />
                                            )}
                                            {cot.estado === 'enviada' && cot.ultima_alerta_estado && (
                                                <div className="kanban-card-alerta">
                                                    <span
                                                        className="kanban-alerta-dot"
                                                        style={{ backgroundColor: getAlertaColor(cot.ultima_alerta_estado) }}
                                                    />
                                                    <span className="kanban-alerta-text">
                                                        {getAlertaNombre(cot.ultima_alerta_estado)}
                                                        {cot.ultima_actividad_tipo && ` - ${cot.ultima_actividad_tipo}`}
                                                    </span>
                                                </div>
                                            )}
                                            {cot.total_actividades > 0 && (
                                                <div className="kanban-card-ultima-actividad">
                                                    Última actividad: {cot.ultima_actividad_fecha && new Date(cot.ultima_actividad_fecha).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    <span className="actividad-count-text"> - {cot.total_actividades} {cot.total_actividades === 1 ? 'registro' : 'registros'}</span>
                                                </div>
                                            )}

                                            <div className="kanban-card-actions">
                                                {/* Quick-view: modal liviano sin salir del kanban (no confundir con detalle completo) */}
                                                <Tooltip content="Vista rápida">
                                                    <button
                                                        className="kanban-action-btn kanban-action-view"
                                                        onClick={(e) => { e.stopPropagation(); handleVerCotizacion(cot.id); }}
                                                        aria-label="Vista rápida"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                </Tooltip>
                                                {/* Botón aparece en enviada/aprobada/rechazada.
                                                    Solo "enviada" permite registrar nueva actividad; en aprobada/rechazada
                                                    el ciclo ya cerró y solo se ve el historial. */}
                                                {cot.estado !== 'borrador' && (
                                                    <Tooltip content={cot.estado === 'enviada' ? 'Registrar actividad / ver historial' : 'Ver historial de actividad'}>
                                                        <button
                                                            className="kanban-action-btn kanban-action-seguimiento"
                                                            onClick={(e) => { e.stopPropagation(); abrirActividadModal(cot); }}
                                                            aria-label="Actividad"
                                                        >
                                                            <MessagesSquare size={16} />
                                                        </button>
                                                    </Tooltip>
                                                )}
                                                {verPdf && (
                                                    <Tooltip content="Previsualizar PDF">
                                                        <button
                                                            className="kanban-action-btn kanban-action-pdf"
                                                            onClick={(e) => { e.stopPropagation(); handleGenerarPdf(cot.id, true); }}
                                                            aria-label="Previsualizar PDF"
                                                        >
                                                            <FileText size={16} />
                                                        </button>
                                                    </Tooltip>
                                                )}
                                                {cot.estado === 'borrador' ? (
                                                    <Tooltip content="Editar cotización">
                                                        <button
                                                            className="kanban-action-btn kanban-action-edit"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/cotizaciones/${cot.id}`); }}
                                                            aria-label="Editar cotización"
                                                        >
                                                            <Pencil size={16} />
                                                        </button>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip content="Ver detalle completo">
                                                        <button
                                                            className="kanban-action-btn kanban-action-detalle"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/cotizaciones/${cot.id}/detalle`); }}
                                                            aria-label="Ver detalle completo"
                                                        >
                                                            <ExternalLink size={16} />
                                                        </button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                        </Tooltip>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Leyenda de flujo de estados — movida al final por ser informativa */}
            <KanbanLegend puedeDesbloquear={puedeDesbloquear} />

            {/* Modal de vista de cotizacion */}
            {(modalCotizacion || loadingModal) && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        {loadingModal ? (
                            <div className="loading">Cargando detalles...</div>
                        ) : modalCotizacion && (
                            <>
                                <div className="modal-header">
                                    <div>
                                        <h3 style={{ margin: 0 }}>Cotizacion {modalCotizacion.numero}</h3>
                                        <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                                            {new Date(modalCotizacion.fecha || modalCotizacion.created_at).toLocaleDateString('es-PE')}
                                        </span>
                                    </div>
                                    <button className="modal-close" onClick={closeModal}>&times;</button>
                                </div>
                                <div className="modal-body">
                                    <div className="cotizacion-detail-grid">
                                        <div className="detail-section">
                                            <h4>Cliente</h4>
                                            <p><strong>{modalCotizacion.cliente_nombre}</strong></p>
                                            {modalCotizacion.cliente_empresa && <p>{modalCotizacion.cliente_empresa}</p>}
                                            {modalCotizacion.cliente_email && <p>{modalCotizacion.cliente_email}</p>}
                                        </div>
                                        <div className="detail-section">
                                            <h4>Estado</h4>
                                            <span className={`badge estado-${modalCotizacion.estado}`}>
                                                {columnas.find(c => c.id === modalCotizacion.estado)?.label || modalCotizacion.estado}
                                            </span>
                                        </div>
                                    </div>
                                    {modalCotizacion.servicios && modalCotizacion.servicios.length > 0 && (
                                        <div className="detail-section">
                                            <h4>Servicios</h4>
                                            {modalCotizacion.servicios.map((serv, idx) => (
                                                <div key={idx} className="service-detail-card">
                                                    <div className="service-detail-header">
                                                        <span className="service-name">{serv.tipo_servicio_nombre}</span>
                                                        <span className={`service-total ${priceClass}`}>{blurPrice(serv.total, modalCotizacion.moneda)}</span>
                                                    </div>
                                                    {serv.asignaciones && serv.asignaciones.length > 0 && (
                                                        <div className="service-breakdown">
                                                            <span className="breakdown-label">Personal:</span>
                                                            {serv.asignaciones.map((a, i) => {
                                                                const horas = parseFloat(a.horas) || 0;
                                                                if (!verDetalles) {
                                                                    return (
                                                                        <div key={i} className="breakdown-item">
                                                                            <span>{a.personal_nombre}</span>
                                                                            <span className={priceClass}>{horas}h = {blurPrice(a.subtotal, modalCotizacion.moneda)}</span>
                                                                        </div>
                                                                    );
                                                                }
                                                                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null
                                                                    ? parseFloat(a.rentabilidad)
                                                                    : 0.68;
                                                                const costoHora = parseFloat(a.costo_hora) || 0;
                                                                const horasAdicionales = parseFloat(a.horas_adicionales) || (horas * 0.12);
                                                                const costo = parseFloat(a.subtotal) || 0;
                                                                const total = rowTotalConVariaciones(costo, rent, a.total_override, a.variaciones);
                                                                const ganancia = total - costo;
                                                                return (
                                                                    <div key={i} className="breakdown-item">
                                                                        <span>{a.personal_nombre}</span>
                                                                        <span>
                                                                            {horas}h
                                                                            {horasAdicionales > 0 && <span className="adicional-text"> +{horasAdicionales.toFixed(2)}</span>}
                                                                            {' '}x {formatCurrency(costoHora, modalCotizacion.moneda)} = {formatCurrency(costo, modalCotizacion.moneda)}
                                                                            <span className="ganancia-text"> + {formatCurrency(ganancia, modalCotizacion.moneda)}</span>
                                                                            <span className="total-text"> = {formatCurrency(total, modalCotizacion.moneda)}</span>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                            {verDetalles && (() => {
                                                                const sumsFilas = serv.asignaciones.reduce((acc, a) => {
                                                                    const rent = a.rentabilidad !== undefined && a.rentabilidad !== null
                                                                        ? parseFloat(a.rentabilidad)
                                                                        : 0.68;
                                                                    const costo = parseFloat(a.subtotal) || 0;
                                                                    const tot = rowTotalConVariaciones(costo, rent, a.total_override, a.variaciones);
                                                                    return { sub: acc.sub + costo, gan: acc.gan + (tot - costo), tot: acc.tot + tot };
                                                                }, { sub: 0, gan: 0, tot: 0 });
                                                                const totConBloque = aplicarVariaciones(sumsFilas.tot, serv.variaciones_bloque_personal || []);
                                                                const sums = { sub: sumsFilas.sub, tot: totConBloque, gan: totConBloque - sumsFilas.sub };
                                                                return (
                                                                    <div className="breakdown-item breakdown-total">
                                                                        <span>Total</span>
                                                                        <span>
                                                                            {formatCurrency(sums.sub, modalCotizacion.moneda)}
                                                                            <span className="ganancia-text"> + {formatCurrency(sums.gan, modalCotizacion.moneda)}</span>
                                                                            <span className="total-text"> = {formatCurrency(sums.tot, modalCotizacion.moneda)}</span>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                    {serv.costosFijos && serv.costosFijos.length > 0 && (
                                                        <div className="service-breakdown">
                                                            <span className="breakdown-label">Costos Fijos:</span>
                                                            {serv.costosFijos.map((cf, i) => {
                                                                const rent = cf.rentabilidad !== undefined && cf.rentabilidad !== null
                                                                    ? parseFloat(cf.rentabilidad)
                                                                    : 0.68;
                                                                const costo = parseFloat(cf.costo) || 0;
                                                                const total = rowTotalConVariaciones(costo, rent, cf.total_override, cf.variaciones);
                                                                if (!verDetalles) {
                                                                    return (
                                                                        <div key={i} className="breakdown-item">
                                                                            <span>{cf.costo_fijo_nombre}</span>
                                                                            <span className={priceClass}>{blurPrice(cf.subtotal, modalCotizacion.moneda)}</span>
                                                                        </div>
                                                                    );
                                                                }
                                                                const ganancia = total - costo;
                                                                return (
                                                                    <div key={i} className="breakdown-item">
                                                                        <span>{cf.costo_fijo_nombre}</span>
                                                                        <span>
                                                                            {formatCurrency(costo, modalCotizacion.moneda)}
                                                                            <span className="ganancia-text"> + {formatCurrency(ganancia, modalCotizacion.moneda)}</span>
                                                                            <span className="total-text"> = {formatCurrency(total, modalCotizacion.moneda)}</span>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                            {verDetalles && (() => {
                                                                const sumsFilas = serv.costosFijos.reduce((acc, cf) => {
                                                                    const rent = cf.rentabilidad !== undefined && cf.rentabilidad !== null
                                                                        ? parseFloat(cf.rentabilidad)
                                                                        : 0.68;
                                                                    const costo = parseFloat(cf.costo) || 0;
                                                                    const tot = rowTotalConVariaciones(costo, rent, cf.total_override, cf.variaciones);
                                                                    return { costo: acc.costo + costo, gan: acc.gan + (tot - costo), tot: acc.tot + tot };
                                                                }, { costo: 0, gan: 0, tot: 0 });
                                                                const totConBloque = aplicarVariaciones(sumsFilas.tot, serv.variaciones_bloque_costos || []);
                                                                const sums = { costo: sumsFilas.costo, tot: totConBloque, gan: totConBloque - sumsFilas.costo };
                                                                return (
                                                                    <div className="breakdown-item breakdown-total">
                                                                        <span>Total</span>
                                                                        <span>
                                                                            {formatCurrency(sums.costo, modalCotizacion.moneda)}
                                                                            <span className="ganancia-text"> + {formatCurrency(sums.gan, modalCotizacion.moneda)}</span>
                                                                            <span className="total-text"> = {formatCurrency(sums.tot, modalCotizacion.moneda)}</span>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {modalCotizacion.extras && modalCotizacion.extras.length > 0 && (
                                        <div className="detail-section">
                                            <h4>Extras</h4>
                                            <div className="detail-table-wrap">
                                                <table className="detail-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Detalle</th>
                                                            <th style={{ textAlign: 'right' }}>Costo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {modalCotizacion.extras.map((extra, idx) => (
                                                            <tr key={idx}>
                                                                <td><div className="detalle-html" dangerouslySetInnerHTML={{ __html: extra.detalle || '' }} /></td>
                                                                <td style={{ textAlign: 'right' }} className={priceClass}>{blurPrice(extra.costo, modalCotizacion.moneda)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {verDetalles && (
                                        <div className="color-legend">
                                            <span className="legend-title">Leyenda:</span>
                                            <span className="legend-item"><span className="legend-dot amarillo"></span>12% Tiempo adicional</span>
                                            <span className="legend-item"><span className="legend-dot verde"></span>Ganancia</span>
                                            <span className="legend-item"><span className="legend-dot azul"></span>Total (personal y costo fijo)</span>
                                        </div>
                                    )}

                                    <div className={`detail-totals ${priceClass}`}>
                                        <div className="total-row">
                                            <span>Subtotal</span>
                                            <span>{blurPrice(modalCotizacion.subtotal, modalCotizacion.moneda)}</span>
                                        </div>
                                        {modalCotizacion.descuento > 0 && (
                                            <div className="total-row">
                                                <span>Descuento</span>
                                                <span>-{blurPrice(modalCotizacion.descuento, modalCotizacion.moneda)}</span>
                                            </div>
                                        )}
                                        {modalCotizacion.incluye_igv !== false && modalCotizacion.incluye_igv !== 0 && (
                                            <div className="total-row">
                                                <span>IGV ({(modalCotizacion.igv_rate || 0.18) * 100}%)</span>
                                                <span>{blurPrice(modalCotizacion.igv, modalCotizacion.moneda)}</span>
                                            </div>
                                        )}
                                        <div className="total-row total-final">
                                            <span>Total</span>
                                            <span>{blurPrice(modalCotizacion.total, modalCotizacion.moneda)}</span>
                                        </div>
                                    </div>

                                    {modalCotizacion.comentarios && (
                                        <div className="detail-section" style={{ marginTop: '16px' }}>
                                            <h4>Comentarios</h4>
                                            <div className="detalle-html" dangerouslySetInnerHTML={{ __html: modalCotizacion.comentarios }} />
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button className="btn ghost" onClick={closeModal}>Cerrar</button>
                                    {/* Editar solo si es borrador. En otros estados va a la vista detalle. */}
                                    {modalCotizacion.estado === 'borrador' ? (
                                        <button className="btn primary" onClick={() => navigate(`/cotizaciones/${modalCotizacion.id}`)}>
                                            Editar cotización
                                        </button>
                                    ) : (
                                        <button className="btn primary" onClick={() => navigate(`/cotizaciones/${modalCotizacion.id}/detalle`)}>
                                            Ver cotización
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal de registrar actividad / historial */}
            {actividadModal && (
                <div className="modal-overlay" onClick={cerrarActividadModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }} role="dialog" aria-modal="true">
                        <div className="modal-header">
                            <div>
                                <h3 style={{ margin: 0 }}>
                                    {actividadModal.estado === 'enviada' && tienePermiso('seguimientos')
                                        ? 'Registrar actividad'
                                        : 'Historial de actividad'}
                                </h3>
                                <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                                    {actividadModal.numero} - {actividadModal.cliente_nombre}
                                </span>
                            </div>
                            <button className="modal-close" onClick={cerrarActividadModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {/* Form solo en cotizaciones ENVIADAS (en proceso de respuesta).
                                Aprobada/rechazada ya cerraron el ciclo → solo se muestra historial. */}
                            {actividadModal.estado === 'enviada' && tienePermiso('seguimientos') && (
                            <div className="actividad-form">
                                <label className="label">Tipo de actividad</label>
                                <div className="actividad-tipo-grid">
                                    {tiposActividad.map(tipo => (
                                        <button
                                            key={tipo.id}
                                            className={`actividad-tipo-btn ${actividadForm.tipo_actividad_id === tipo.id ? 'selected' : ''}`}
                                            onClick={() => setActividadForm(prev => ({ ...prev, tipo_actividad_id: tipo.id }))}
                                        >
                                            {tipo.nombre}
                                        </button>
                                    ))}
                                </div>

                                <label className="field">
                                    <span className="label">Descripcion breve</span>
                                    <input
                                        className="input"
                                        type="text"
                                        value={actividadForm.descripcion}
                                        onChange={(e) => setActividadForm(prev => ({ ...prev, descripcion: e.target.value }))}
                                        placeholder="Ej: Se conversó sobre la propuesta, queda en revisar"
                                        maxLength={500}
                                    />
                                </label>

                                <label className="label">Estado de seguimiento</label>
                                <div className="actividad-estado-btns">
                                    {alertasConfig.filter(a => a.clave === 'comunicacion_reciente' || a.clave === 'pendiente_respuesta').map(alerta => (
                                        <button
                                            key={alerta.id}
                                            className={`actividad-estado-btn ${actividadForm.estado_alerta === alerta.clave ? 'selected' : ''}`}
                                            style={{ '--estado-color': alerta.color }}
                                            onClick={() => setActividadForm(prev => ({ ...prev, estado_alerta: alerta.clave }))}
                                        >
                                            <span className="actividad-estado-dot" style={{ backgroundColor: alerta.color }} />
                                            {alerta.nombre}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="btn neon"
                                    onClick={guardarActividad}
                                    disabled={savingActividad}
                                    style={{ marginTop: '4px' }}
                                >
                                    {savingActividad ? 'Guardando...' : 'Registrar Actividad'}
                                </button>
                            </div>
                            )}

                            {/* Historial */}
                            <div style={{ marginTop: '20px' }}>
                                <h4 style={{ color: 'var(--text)', marginBottom: '8px' }}>Historial de actividades</h4>
                                {loadingHistorial ? (
                                    <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Cargando...</p>
                                ) : historial.length === 0 ? (
                                    <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Sin actividades registradas</p>
                                ) : (
                                    <div className="actividad-historial">
                                        {historial.map(act => (
                                            <div key={act.id} className="actividad-item">
                                                <div className="actividad-item-dot" />
                                                <div className="actividad-item-content">
                                                    <div className="actividad-item-tipo">{act.tipo_nombre}</div>
                                                    {act.descripcion && (
                                                        <div className="actividad-item-desc">{act.descripcion}</div>
                                                    )}
                                                    <div className="actividad-item-meta">
                                                        {act.usuario_nombre} - {new Date(act.created_at).toLocaleDateString('es-PE')} {new Date(act.created_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <HiloVersionesModal
                open={!!hiloModal}
                cotizacionId={hiloModal}
                onClose={() => setHiloModal(null)}
                onChange={cargarDatos}
            />
        </div>
    );
};

export default MisCotizaciones;
